import { Injectable, inject } from '@angular/core';
import {
  UploadRepositoryService,
  Upload,
  NewUpload,
  UpdateUpload,
  VerifyStorageAvailableResponse,
  TotalStorageUsageResponse,
  BaseTag,
  Utilities
} from '@xmedical/core';
import { Observable } from 'rxjs';
import { HttpMethod, UploadType } from '../models/upload-type.type';
import { UploadsStore } from '../state/uploads.store';

/**
 * FileUploaderService
 *
 * Facade service for upload operations.
 * - File mechanics (signed URLs, S3 upload) stay here
 * - CRUD and state operations delegate to UploadsStore
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploaderService {
  private readonly uploadRepository = inject(UploadRepositoryService);
  private readonly uploadsStore = inject(UploadsStore);

  generateUuid(): string {
    return Utilities.generateUuid();
  }

  // ============================================================
  // Signed URL Methods (file mechanics - unchanged)
  // ============================================================

  getSignedUrl(file: string, method: HttpMethod, expires: number): Observable<string> {
    return this.uploadRepository.getSignedUrl(file, method, expires);
  }

  getSignedReportsUrl(file: string, method: HttpMethod, expires: number): Observable<string> {
    return this.uploadRepository.getSignedReportsUrl(file, method, expires);
  }

  getSignedUploadsUrl(file: string, method: HttpMethod, expires: number): Observable<string> {
    return this.uploadRepository.getSignedUploadsUrl(file, method, expires);
  }

  getSignedAssetsUrl(file: string, method: HttpMethod, expires: number): Observable<string> {
    return this.uploadRepository.getSignedAssetsUrl(file, method, expires);
  }

  uploadFile(file: File, url: string): Observable<string> {
    return this.uploadRepository.uploadFile(file, url);
  }

  // ============================================================
  // CRUD Operations (delegate to store)
  // ============================================================

  saveUpload(upload: NewUpload, type: UploadType): Observable<number> {
    return this.uploadsStore.createUpload(upload);
  }

  updateUpload(uploadId: number, upload: UpdateUpload): Observable<boolean> {
    return this.uploadsStore.updateUpload(uploadId, upload);
  }

  deleteUpload(uploadId: number, patientId?: number): Observable<boolean> {
    return this.uploadsStore.deleteUpload(uploadId, patientId);
  }

  // ============================================================
  // Query Operations (delegate to store)
  // ============================================================

  getTotalStorageUsage(): Observable<TotalStorageUsageResponse> {
    return this.uploadsStore.loadStorageUsage();
  }

  verifyStorageAvailable(size: number): Observable<VerifyStorageAvailableResponse> {
    return this.uploadsStore.verifyStorageAvailable(size);
  }

  queryTags(query: string): Observable<BaseTag[]> {
    return this.uploadsStore.queryTags(query);
  }

  loadUploads(patientId: number, type: UploadType, tags: string[]): Observable<Upload[]> {
    return this.uploadsStore.loadPatientUploads(patientId, tags);
  }
}
